import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Droplets, Plus, RefreshCw, Loader2, Trash2, Edit, Calendar,
  Building2, CheckCircle, AlertTriangle, Clock, X, Filter, Search,
  Bell, User, History, Truck, Paperclip, Upload, Download, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import AdminDITankExchange from "@/components/admin/AdminDITankExchange";
import AdminDITankCatalogEditor from "@/components/admin/AdminDITankCatalogEditor";
import DIPoDispatchDialog from "@/components/admin/DIPoDispatchDialog";

const TANK_TYPES = ["Standard DI", "Mixed Bed DI", "Cation", "Anion", "Carbon"];
const TANK_SIZES = ["0.5 cu. ft.", "1.0 cu. ft.", "1.6 cu. ft.", "2.0 cu. ft.", "3.6 cu. ft."];

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

function formatTs(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

// ========================
// AUDIT LOG DIALOG
// ========================
function AuditLogDialog({ open, onClose, deploymentId, token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && deploymentId) {
      setLoading(true);
      fetch(`${API}/di-tanks/deployments/${deploymentId}/change-log`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json()).then(d => {
        setLogs(d.change_log || []);
      }).catch(() => setLogs([]))
      .finally(() => setLoading(false));
    }
  }, [open, deploymentId, token]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="audit-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-slate-500" /> Change Log — {deploymentId}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-3">
          {loading && <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin mx-auto text-cyan-600" /></div>}
          {!loading && logs.length === 0 && <p className="text-slate-400 text-center py-6">No change history.</p>}
          {logs.slice().reverse().map((entry, i) => (
            <div key={i} className="border-l-2 border-cyan-300 pl-4 py-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded">{entry.user}</span>
                <span className="text-xs text-slate-400">{formatTs(entry.timestamp)}</span>
              </div>
              <p className="text-sm text-slate-600 mt-1">
                <span className="font-medium capitalize">{entry.action?.replace(/_/g, " ")}</span>
                {entry.details && <span className="text-slate-400"> — {entry.details}</span>}
              </p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ========================
// ATTACHMENTS DIALOG
// ========================
function AttachmentsDialog({ open, onClose, deployment, token, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const attachments = deployment?.attachments || [];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploaded_by_role", "admin");
    try {
      const res = await fetch(`${API}/di-tanks/deployments/${deployment.id}/attachments`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed");
      toast.success(`Uploaded: ${file.name}`);
      onRefresh();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleDelete = async (attId) => {
    if (!confirm("Delete this attachment?")) return;
    const res = await fetch(`${API}/di-tanks/deployments/${deployment.id}/attachments/${attId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    });
    if (res.ok) { toast.success("Deleted"); onRefresh(); }
    else toast.error("Delete failed");
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const roleColor = (role) => {
    if (role === "admin") return "bg-blue-100 text-blue-700";
    if (role === "contractor") return "bg-orange-100 text-orange-700";
    if (role === "hospital") return "bg-green-100 text-green-700";
    return "bg-slate-100 text-slate-700";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="attachments-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-cyan-600" />
            Work Order Attachments — {deployment?.id}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">{deployment?.hospital_name} / {deployment?.department}</p>

        {/* Upload */}
        <div className="border-2 border-dashed border-cyan-200 rounded-lg p-4 text-center hover:border-cyan-400 transition-colors">
          <label className="cursor-pointer block" data-testid="upload-attachment-input">
            <input type="file" className="hidden" onChange={handleUpload} accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.csv,.txt" />
            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-cyan-600" />
            ) : (
              <>
                <Upload className="w-6 h-6 mx-auto text-cyan-400 mb-2" />
                <p className="text-sm text-cyan-700 font-medium">Click to upload work order</p>
                <p className="text-xs text-slate-400 mt-1">PDF, DOC, XLSX, images (max 25MB)</p>
              </>
            )}
          </label>
        </div>

        {/* File List */}
        <div className="space-y-2 max-h-[40vh] overflow-y-auto">
          {attachments.length === 0 ? (
            <p className="text-center text-slate-400 py-6 text-sm">No attachments yet.</p>
          ) : attachments.map(att => (
            <div key={att.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 group">
              <FileText className="w-8 h-8 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{att.original_name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${roleColor(att.uploaded_by_role)}`}>
                    {att.uploaded_by_role}
                  </span>
                  <span className="text-xs text-slate-400">{att.uploaded_by_name}</span>
                  <span className="text-xs text-slate-300">|</span>
                  <span className="text-xs text-slate-400">{formatSize(att.size_bytes)}</span>
                  <span className="text-xs text-slate-300">|</span>
                  <span className="text-xs text-slate-400">{formatTs(att.uploaded_at)}</span>
                </div>
              </div>
              <a href={`${API}/di-tanks/attachments/${att.stored_name}`} target="_blank" rel="noreferrer"
                className="p-1.5 rounded hover:bg-cyan-100 text-cyan-600" title="Download">
                <Download className="w-4 h-4" />
              </a>
              <button onClick={() => handleDelete(att.id)}
                className="p-1.5 rounded hover:bg-red-100 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" title="Delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ========================
// DEPLOYMENT FORM DIALOG
// ========================
function DeploymentFormDialog({ open, onClose, onSave, hospitals, suppliers, editData }) {
  const isEdit = !!editData;
  const [form, setForm] = useState({
    hospital_id: "", hospital_name: "", location: "", department: "",
    contact_name: "", contact_email: "",
    tank_type: "Standard DI", tank_size: "1.6 cu. ft.",
    installation_date: new Date().toISOString().split("T")[0],
    service_type: "new", contractor_supplier: "", technician_name: "", notes: ""
  });
  const [newHospital, setNewHospital] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editData) {
      setForm({
        hospital_id: editData.hospital_id || "", hospital_name: editData.hospital_name || "",
        location: editData.location || "", department: editData.department || "",
        contact_name: editData.contact_name || "", contact_email: editData.contact_email || "",
        tank_type: editData.tank_type || "Standard DI",
        tank_size: editData.tank_size || "1.6 cu. ft.", installation_date: editData.installation_date || "",
        service_type: editData.service_type || "new",
        contractor_supplier: editData.contractor_supplier || "",
        technician_name: editData.technician_name || "",
        notes: editData.notes || ""
      });
      setNewHospital(!hospitals.some(h => h.name === editData.hospital_name));
    } else {
      setForm({
        hospital_id: "", hospital_name: "", location: "", department: "",
        contact_name: "", contact_email: "",
        tank_type: "Standard DI", tank_size: "1.6 cu. ft.",
        installation_date: new Date().toISOString().split("T")[0],
        service_type: "new", contractor_supplier: "", technician_name: "", notes: ""
      });
      setNewHospital(false);
    }
  }, [editData, open, hospitals]);

  const exchangeDate = form.installation_date ? addDays(form.installation_date, 84) : "";

  const handleHospitalSelect = (val) => {
    if (val === "__new__") {
      setNewHospital(true);
      setForm(f => ({ ...f, hospital_id: "", hospital_name: "" }));
    } else {
      const h = hospitals.find(x => x.id === val);
      setNewHospital(false);
      setForm(f => ({ ...f, hospital_id: val, hospital_name: h?.name || "" }));
    }
  };

  const handleSubmit = async () => {
    if (!form.hospital_name || !form.department || !form.installation_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try { await onSave(form, isEdit); onClose(); }
    catch { /* handled upstream */ }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="deployment-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-cyan-600" />
            {isEdit ? "Edit DI Tank Deployment" : "New DI Tank Deployment"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          {/* Hospital Selection */}
          <div>
            <Label>Healthcare Organization *</Label>
            {!newHospital ? (
              <Select value={form.hospital_id || undefined} onValueChange={handleHospitalSelect}>
                <SelectTrigger data-testid="hospital-select"><SelectValue placeholder="Select organization..." /></SelectTrigger>
                <SelectContent>
                  {hospitals.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                  <SelectItem value="__new__">+ Add New Organization</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex gap-2">
                <Input value={form.hospital_name} onChange={e => setForm(f => ({ ...f, hospital_name: e.target.value }))}
                  placeholder="Enter organization name" data-testid="new-hospital-input" />
                <Button variant="ghost" size="sm" onClick={() => setNewHospital(false)}><X className="w-4 h-4" /></Button>
              </div>
            )}
          </div>

          {/* Location (specific site within health network) */}
          <div>
            <Label>Location / Site</Label>
            <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              placeholder="e.g. Lyndon B Johnson Hospital, Ben Taub Hospital" data-testid="location-input" />
          </div>

          {/* Department */}
          <div>
            <Label>Department *</Label>
            <Input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
              placeholder="e.g. Sterile Processing 4th Floor Water Purification Room" data-testid="department-input" />
          </div>

          {/* Hospital Contact */}
          <div className="border-t pt-4 mt-2">
            <p className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2"><User className="w-4 h-4" /> Hospital Contact</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                  placeholder="Facility contact person" data-testid="contact-name-input" />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                  placeholder="contact@hospital.org" data-testid="contact-email-input" />
                <p className="text-xs text-slate-400 mt-1">Will receive deployment notification &amp; exchange reminders</p>
              </div>
            </div>
          </div>

          {/* Tank Type & Size */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tank Type</Label>
              <Select value={form.tank_type} onValueChange={v => setForm(f => ({ ...f, tank_type: v }))}>
                <SelectTrigger data-testid="tank-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TANK_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tank Size</Label>
              <Select value={form.tank_size} onValueChange={v => setForm(f => ({ ...f, tank_size: v }))}>
                <SelectTrigger data-testid="tank-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TANK_SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Service Type */}
          <div>
            <Label>Service Type</Label>
            <Select value={form.service_type} onValueChange={v => setForm(f => ({ ...f, service_type: v }))}>
              <SelectTrigger data-testid="service-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New Installation</SelectItem>
                <SelectItem value="recurring">Recurring Exchange</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Installation Date & Exchange Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Installation Date *</Label>
              <Input type="date" value={form.installation_date}
                onChange={e => setForm(f => ({ ...f, installation_date: e.target.value }))} data-testid="install-date-input" />
            </div>
            <div>
              <Label>Next Exchange Date (84 days)</Label>
              <Input type="date" value={exchangeDate} disabled className="bg-slate-50" data-testid="exchange-date-display" />
            </div>
          </div>

          {/* Contractor/Supplier & Technician */}
          <div className="border-t pt-4 mt-2">
            <p className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> Service Personnel</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contractor / Supplier</Label>
                <Select value={form.contractor_supplier || "__none__"} onValueChange={v => setForm(f => ({ ...f, contractor_supplier: v === "__none__" ? "" : v }))}>
                  <SelectTrigger data-testid="contractor-select"><SelectValue placeholder="Select or type..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!suppliers.some(s => s.name === form.contractor_supplier) && form.contractor_supplier && (
                  <p className="text-xs text-slate-400 mt-1">Custom: {form.contractor_supplier}</p>
                )}
                <Input value={form.contractor_supplier} onChange={e => setForm(f => ({ ...f, contractor_supplier: e.target.value }))}
                  placeholder="Or type contractor name" className="mt-2" data-testid="contractor-input" />
              </div>
              <div>
                <Label>Technician Name (optional)</Label>
                <Input value={form.technician_name} onChange={e => setForm(f => ({ ...f, technician_name: e.target.value }))}
                  placeholder="Technician performing exchange" data-testid="technician-input" />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Additional details..." rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-cyan-700 hover:bg-cyan-800" data-testid="save-deployment-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {isEdit ? "Update" : "Create"} Deployment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========================
// MAIN COMPONENT
// ========================
export default function DITankManagement() {
  const { token } = useAuth();
  const [deployments, setDeployments] = useState([]);
  const [exchangeRequests, setExchangeRequests] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingDeploy, setEditingDeploy] = useState(null);
  const [activeTab, setActiveTab] = useState("deployments");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterHospital, setFilterHospital] = useState("all");
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditLogDeployId, setAuditLogDeployId] = useState(null);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [poDeployId, setPoDeployId] = useState(null);
  const [attachDeploy, setAttachDeploy] = useState(null);
  const [linkingId, setLinkingId] = useState(null);

  // Hand a DI record to the Sourcing Desk (Build Quote → Build Order →
  // Fulfillment → Invoice). Created once per record, reopened after that.
  const openInSourcingDesk = async (source, sourceId) => {
    setLinkingId(sourceId);
    try {
      const res = await fetch(`${API}/di-tanks/sourcing-request`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ source, source_id: sourceId }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Could not open a sourcing request");
      toast.success(d.created
        ? `Sourcing request ${d.backorder_id} created — opening Build Quote`
        : `Opening sourcing request ${d.backorder_id}`);
      window.location.assign(`/admin/backorders?open=${d.backorder_id}&tab=build`);
    } catch (e) {
      toast.error(e.message || "Could not open a sourcing request");
      setLinkingId(null);
    }
  };

  const QuoteOrderButton = ({ source, id, linked }) => (
    <Button
      variant="outline"
      size="sm"
      onClick={(e) => { e.stopPropagation(); openInSourcingDesk(source, id); }}
      disabled={linkingId === id}
      className="text-cyan-700 border-cyan-300 hover:bg-cyan-50 gap-1"
      title={linked
        ? `Open sourcing request ${linked} — quote, order, ship and invoice`
        : "Open this in the Sourcing Desk to quote, order, ship and invoice it"}
      data-testid={`di-quote-order-${id}`}
    >
      {linkingId === id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
      {linked ? "Open Order" : "Quote / Order"}
    </Button>
  );

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [depRes, reqRes, hsRes, supRes] = await Promise.all([
        fetch(`${API}/di-tanks/deployments`, { headers }),
        fetch(`${API}/di-tanks/exchange-requests`, { headers }),
        fetch(`${API}/di-tanks/health-systems-list`, { headers }),
        fetch(`${API}/di-tanks/suppliers-list`, { headers }),
      ]);
      if (depRes.ok) setDeployments(await depRes.json());
      if (reqRes.ok) setExchangeRequests(await reqRes.json());
      if (hsRes.ok) setHospitals(await hsRes.json());
      if (supRes.ok) setSuppliers(await supRes.json());
    } catch {
      toast.error("Failed to load DI tank data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSave = async (form, isEdit) => {
    const url = isEdit ? `${API}/di-tanks/deployments/${editingDeploy.id}` : `${API}/di-tanks/deployments`;
    const method = isEdit ? "PATCH" : "POST";
    const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
    if (!res.ok) { const d = await res.json(); throw toast.error(d.detail || "Save failed"); }
    toast.success(isEdit ? "Deployment updated" : "Deployment created");
    setEditingDeploy(null);
    fetchAll();
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this deployment record?")) return;
    const res = await fetch(`${API}/di-tanks/deployments/${id}`, { method: "DELETE", headers });
    if (res.ok) { toast.success("Deleted"); fetchAll(); } else toast.error("Delete failed");
  };

  const handleRequestStatus = async (id, status) => {
    const res = await fetch(`${API}/di-tanks/exchange-requests/${id}/status`, {
      method: "PATCH", headers, body: JSON.stringify({ status })
    });
    if (res.ok) { toast.success(`Request ${status}`); fetchAll(); } else toast.error("Update failed");
  };

  const handleSendNotifications = async () => {
    setSendingNotifications(true);
    try {
      const res = await fetch(`${API}/di-tanks/check-notifications`, { method: "POST", headers });
      const data = await res.json();
      if (data.notifications_sent > 0) {
        toast.success(`Sent ${data.notifications_sent} exchange reminder(s)`);
      } else {
        toast.info("No upcoming exchanges need notifications right now");
      }
      fetchAll();
    } catch { toast.error("Failed to send notifications"); }
    finally { setSendingNotifications(false); }
  };

  const uniqueHospitals = useMemo(() => [...new Set(deployments.map(d => d.hospital_name))].sort(), [deployments]);

  const filteredDeployments = useMemo(() => {
    let list = deployments;
    if (filterHospital !== "all") list = list.filter(d => d.hospital_name === filterHospital);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(d =>
        d.hospital_name?.toLowerCase().includes(q) || d.department?.toLowerCase().includes(q) ||
        d.id?.toLowerCase().includes(q) || d.contractor_supplier?.toLowerCase().includes(q) ||
        d.technician_name?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [deployments, filterHospital, searchTerm]);

  const stats = useMemo(() => ({
    total: deployments.length,
    active: deployments.filter(d => d.status === "active").length,
    urgent: deployments.filter(d => d.exchange_urgent).length,
    overdue: deployments.filter(d => d.exchange_overdue).length,
    pendingRequests: exchangeRequests.filter(r => r.status === "pending").length,
  }), [deployments, exchangeRequests]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cyan-600" /></div>;

  return (
    <div className="space-y-6" data-testid="di-tank-management">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { val: stats.total, label: "Total Tanks", color: "cyan" },
          { val: stats.active, label: "Active", color: "green" },
          { val: stats.urgent, label: "Exchange Soon", color: "amber" },
          { val: stats.overdue, label: "Overdue", color: "red" },
          { val: stats.pendingRequests, label: "Pending Requests", color: "blue" },
        ].map(s => (
          <Card key={s.label} className={`border-${s.color}-200 bg-${s.color}-50`}>
            <CardContent className="p-4 text-center">
              <p className={`text-3xl font-bold text-${s.color}-800`}>{s.val}</p>
              <p className={`text-sm text-${s.color}-600`}>{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs & Actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          <Button variant={activeTab === "deployments" ? "default" : "outline"}
            onClick={() => setActiveTab("deployments")}
            className={activeTab === "deployments" ? "bg-cyan-700 hover:bg-cyan-800" : ""}
            data-testid="deployments-tab">
            <Droplets className="w-4 h-4 mr-2" /> Deployments
          </Button>
          <Button variant={activeTab === "requests" ? "default" : "outline"}
            onClick={() => setActiveTab("requests")}
            className={activeTab === "requests" ? "bg-cyan-700 hover:bg-cyan-800" : ""}
            data-testid="requests-tab">
            <Calendar className="w-4 h-4 mr-2" /> Hospital Portal Requests
            {stats.pendingRequests > 0 && <Badge className="ml-2 bg-red-500 text-white">{stats.pendingRequests}</Badge>}
          </Button>
          <Button variant={activeTab === "public_orders" ? "default" : "outline"}
            onClick={() => setActiveTab("public_orders")}
            className={activeTab === "public_orders" ? "bg-cyan-700 hover:bg-cyan-800" : ""}
            data-testid="public-orders-tab">
            <Truck className="w-4 h-4 mr-2" /> Public Exchange Orders
          </Button>
          <Button variant={activeTab === "catalog" ? "default" : "outline"}
            onClick={() => setActiveTab("catalog")}
            className={activeTab === "catalog" ? "bg-cyan-700 hover:bg-cyan-800" : ""}
            data-testid="catalog-editor-tab">
            <Edit className="w-4 h-4 mr-2" /> Catalog Editor
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSendNotifications} disabled={sendingNotifications}
            className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="send-notifications-btn">
            {sendingNotifications ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
            Send Reminders
          </Button>
          <Button variant="outline" onClick={fetchAll} size="sm"><RefreshCw className="w-4 h-4" /></Button>
          {activeTab === "deployments" && (
            <Button onClick={() => { setEditingDeploy(null); setFormOpen(true); }} className="bg-cyan-700 hover:bg-cyan-800" data-testid="new-deployment-btn">
              <Plus className="w-4 h-4 mr-2" /> New Deployment
            </Button>
          )}
        </div>
      </div>

      {/* DEPLOYMENTS TAB */}
      {activeTab === "deployments" && (
        <>
          <div className="flex gap-3 items-center">
            <div className="relative flex-1 max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search hospital, department, contractor..." className="pl-9" data-testid="search-deployments" />
            </div>
            <Select value={filterHospital} onValueChange={setFilterHospital}>
              <SelectTrigger className="w-56" data-testid="filter-hospital">
                <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="All Hospitals" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Organizations</SelectItem>
                {uniqueHospitals.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Location / Dept</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Tank</TableHead>
                    <TableHead>Contractor / Tech</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Installed</TableHead>
                    <TableHead>Next Exchange</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDeployments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center py-12 text-slate-500">
                        <Droplets className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                        No deployments found. Click "New Deployment" to add one.
                      </TableCell>
                    </TableRow>
                  ) : filteredDeployments.map(dep => (
                    <TableRow key={dep.id} className={dep.exchange_overdue ? "bg-red-50" : dep.exchange_urgent ? "bg-amber-50" : ""}>
                      <TableCell className="font-mono text-xs text-slate-500">{dep.id}</TableCell>
                      <TableCell className="font-medium">{dep.hospital_name}</TableCell>
                      <TableCell>
                        {dep.location && <span className="text-sm text-slate-700">{dep.location}</span>}
                        {dep.location && dep.department && <br />}
                        <span className="text-xs text-slate-500">{dep.department}</span>
                      </TableCell>
                      <TableCell>
                        {dep.contact_email ? (
                          <div>
                            {dep.contact_name && <span className="text-sm text-slate-700">{dep.contact_name}</span>}
                            <br /><a href={`mailto:${dep.contact_email}`} className="text-xs text-cyan-600 hover:underline">{dep.contact_email}</a>
                          </div>
                        ) : <span className="text-xs text-slate-400 italic">No contact</span>}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{dep.tank_type}</span><br />
                        <span className="text-xs text-slate-500">{dep.tank_size}</span>
                      </TableCell>
                      <TableCell>
                        {dep.contractor_supplier ? (
                          <div>
                            <span className="text-sm flex items-center gap-1"><Truck className="w-3 h-3 text-slate-400" />{dep.contractor_supplier}</span>
                            {dep.technician_name && <span className="text-xs text-slate-500 flex items-center gap-1"><User className="w-3 h-3" />{dep.technician_name}</span>}
                          </div>
                        ) : <span className="text-xs text-slate-400 italic">Not assigned</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant={dep.service_type === "new" ? "default" : "secondary"}>
                          {dep.service_type === "new" ? "New" : "Recurring"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{dep.installation_date}</TableCell>
                      <TableCell>
                        <div className="text-sm">{dep.exchange_date}</div>
                        {dep.exchange_overdue && <span className="text-xs text-red-600 font-medium">Overdue by {Math.abs(dep.days_until_exchange)} days</span>}
                        {!dep.exchange_overdue && dep.exchange_urgent && <span className="text-xs text-amber-600 font-medium">{dep.days_until_exchange} days left</span>}
                        {!dep.exchange_overdue && !dep.exchange_urgent && dep.days_until_exchange != null && <span className="text-xs text-slate-400">{dep.days_until_exchange} days</span>}
                      </TableCell>
                      <TableCell>
                        {dep.exchange_overdue ? <Badge className="bg-red-100 text-red-700 border-red-200">Overdue</Badge>
                          : dep.exchange_urgent ? <Badge className="bg-amber-100 text-amber-700 border-amber-200">Due Soon</Badge>
                          : <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <QuoteOrderButton source="deployment" id={dep.id} linked={dep.sourcing_request_id} />
                          <Button variant="ghost" size="sm" title="Send exchange PO to vendors"
                            onClick={() => setPoDeployId(dep.id)}
                            data-testid={`di-dep-send-po-${dep.id}`}>
                            <FileText className="w-4 h-4 text-cyan-600" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Attachments"
                            onClick={() => { setAttachDeploy(dep); setAttachOpen(true); }}
                            className="relative" data-testid={`attach-${dep.id}`}>
                            <Paperclip className="w-4 h-4 text-slate-400" />
                            {dep.attachments?.length > 0 && (
                              <span className="absolute -top-1 -right-1 bg-cyan-600 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                                {dep.attachments.length}
                              </span>
                            )}
                          </Button>
                          <Button variant="ghost" size="sm" title="Change Log"
                            onClick={() => { setAuditLogDeployId(dep.id); setAuditLogOpen(true); }}>
                            <History className="w-4 h-4 text-slate-400" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setEditingDeploy(dep); setFormOpen(true); }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(dep.id)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Created-by footer info */}
          {filteredDeployments.length > 0 && (
            <p className="text-xs text-slate-400 text-right">
              Records show mandatory audit stamp. Click <History className="w-3 h-3 inline" /> to view full change history.
            </p>
          )}
        </>
      )}

      {/* EXCHANGE REQUESTS TAB */}
      {activeTab === "requests" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5 text-cyan-600" /> Hospital Exchange Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Hospital</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Tank</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Requested Date</TableHead>
                  <TableHead>Submitted By</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exchangeRequests.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-500">No exchange requests yet.</TableCell></TableRow>
                ) : exchangeRequests.map(req => (
                  <TableRow key={req.id}>
                    <TableCell className="font-mono text-xs text-slate-500">{req.id}</TableCell>
                    <TableCell className="font-medium">{req.hospital_name}</TableCell>
                    <TableCell>{req.department}</TableCell>
                    <TableCell>
                      <span className="text-sm">{req.tank_type}</span><br />
                      <span className="text-xs text-slate-500">{req.tank_size}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={req.service_type === "new" ? "default" : "secondary"}>
                        {req.service_type === "new" ? "New" : "Exchange"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{req.requested_date}</TableCell>
                    <TableCell className="text-xs text-slate-500">{req.submitted_by || "—"}</TableCell>
                    <TableCell>
                      <Badge className={
                        req.status === "pending" ? "bg-amber-100 text-amber-700" :
                        req.status === "scheduled" ? "bg-blue-100 text-blue-700" :
                        req.status === "completed" ? "bg-green-100 text-green-700" :
                        "bg-slate-100 text-slate-700"
                      }>{req.status?.charAt(0).toUpperCase() + req.status?.slice(1)}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1 flex-wrap">
                        <QuoteOrderButton source="exchange_request" id={req.id} linked={req.sourcing_request_id} />
                        {req.status === "pending" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleRequestStatus(req.id, "scheduled")}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50">Schedule</Button>
                            <Button size="sm" variant="outline" onClick={() => handleRequestStatus(req.id, "completed")}
                              className="text-green-600 border-green-200 hover:bg-green-50">Complete</Button>
                          </>
                        )}
                        {req.status === "scheduled" && (
                          <Button size="sm" variant="outline" onClick={() => handleRequestStatus(req.id, "completed")}
                            className="text-green-600 border-green-200 hover:bg-green-50">Mark Complete</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* PUBLIC EXCHANGE ORDERS TAB — from /solutions/water-treatment form */}
      {activeTab === "public_orders" && (
        <AdminDITankExchange token={token} onQuoteOrder={openInSourcingDesk} linkingId={linkingId} />
      )}

      {/* CATALOG EDITOR TAB — edit pricing/badges/descriptions for DI tank products */}
      {activeTab === "catalog" && (
        <AdminDITankCatalogEditor token={token} />
      )}

      {/* Dialogs */}
      <DeploymentFormDialog open={formOpen} onClose={() => { setFormOpen(false); setEditingDeploy(null); }}
        onSave={handleSave} hospitals={hospitals} suppliers={suppliers} editData={editingDeploy} />
      <AuditLogDialog open={auditLogOpen} onClose={() => setAuditLogOpen(false)}
        deploymentId={auditLogDeployId} token={token} />
      <AttachmentsDialog open={attachOpen} onClose={() => { setAttachOpen(false); setAttachDeploy(null); }}
        deployment={attachDeploy} token={token} onRefresh={fetchAll} />
      {poDeployId && (
        <DIPoDispatchDialog
          deploymentId={poDeployId}
          token={token}
          onClose={() => setPoDeployId(null)}
          onSent={fetchAll}
        />
      )}
    </div>
  );
}
